import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, LogOut, ShieldCheck, KeyRound, Users, Eye } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { ManageAccessDialog } from "@/components/ManageAccessDialog";
import { getViewers } from "@/lib/api";
import { toast } from "sonner";

const btnCls =
  "flex items-center gap-1.5 border border-[#0A1F44]/30 px-3 py-2 text-xs uppercase tracking-wider text-[#0A1F44] hover:bg-[#E6EAF1] transition-colors";

export const Header = ({ children }) => {
  const { isAdmin, isViewer, signOut } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const [viewerCount, setViewerCount] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    getViewers().then((v) => setViewerCount(v.length)).catch(() => {});
  }, [isAdmin, accessOpen]);

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-40 bg-[#F3F5F8]/90 backdrop-blur-xl border-b border-[#0A1F44]/20 px-5 sm:px-8 py-4 flex justify-between items-center gap-3"
    >
      <Link to="/" data-testid="header-home-link" className="flex items-center gap-3 group shrink-0">
        <div className="w-9 h-9 bg-[#0A1F44] text-[#F3F5F8] flex items-center justify-center group-hover:bg-[#C0982E] transition-colors duration-300">
          <GraduationCap className="w-5 h-5" strokeWidth={1.5} />
        </div>
        <div className="leading-none">
          <div className="font-serif text-xl text-[#0A1F44]">North East Fine Arts</div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/50">
            Staff Directory
          </div>
        </div>
      </Link>

      <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
        {children}
        {isAdmin && (
          <>
            <span data-testid="admin-badge" className="hidden md:flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#0A1F44]/60">
              <ShieldCheck className="w-3.5 h-3.5 text-[#C0982E]" /> Admin
            </span>
            {viewerCount !== null && (
              <button
                data-testid="viewer-access-count"
                onClick={() => setAccessOpen(true)}
                title="Manage who can access the directory"
                className="hidden sm:flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#0A1F44]/50 hover:text-[#C0982E] transition-colors"
              >
                <Users className="w-3.5 h-3.5" />
                {viewerCount} coworker{viewerCount === 1 ? "" : "s"} {viewerCount === 1 ? "has" : "have"} access
              </button>
            )}
            <button data-testid="manage-access-btn" onClick={() => setAccessOpen(true)} className={btnCls}>
              <Users className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Access</span>
            </button>
            <button data-testid="change-password-btn" onClick={() => setPwOpen(true)} className={btnCls}>
              <KeyRound className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Password</span>
            </button>
          </>
        )}
        {isViewer && (
          <span data-testid="viewer-badge" className="hidden md:flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#0A1F44]/60">
            <Eye className="w-3.5 h-3.5 text-[#C0982E]" /> View Only
          </span>
        )}
        <button
          data-testid="signout-btn"
          onClick={() => { signOut(); toast.success("Signed out"); }}
          className={btnCls}
        >
          <LogOut className="w-3.5 h-3.5" /> Sign Out
        </button>
      </div>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <ManageAccessDialog open={accessOpen} onOpenChange={setAccessOpen} />
    </header>
  );
};
